import Bugsnag, { OnErrorCallback } from "@bugsnag/js"
import BugsnagPluginReact from "@bugsnag/plugin-react"
import getConfig from "next/config"

const { publicRuntimeConfig } = getConfig()

/**
 * Resolves the Bugsnag API key for the current runtime environment.
 * Server-side: reads from the Kubernetes secret via `k8sSecrets.server` (requires `fs`).
 * Client-side: reads from `publicRuntimeConfig` (injected at build time).
 *
 * The dynamic `require` keeps `fs` out of the client bundle — webpack dead-code-eliminates
 * the server branch when `typeof window !== "undefined"`.
 */
function resolveApiKey(): string | undefined {
  if (typeof window === "undefined") {
    // Dynamic require keeps fs out of the client bundle (webpack dead-code-eliminates this branch)
    const { serverBugsnagApiKey } = require("../../k8sSecrets.server")
    return serverBugsnagApiKey()
  }
  return publicRuntimeConfig.BUGSNAG_API_KEY
}

const apiKey = resolveApiKey()

/**
 * Discard errors originating from Google Translate proxied pages.
 * Google Translate serves our site under *.translate.goog domains. Bugsnag's
 * Allowed Domains config only checks the reporting domain, not the page URL,
 * so translate.goog events bypass that filter and create significant noise.
 * Returning false tells Bugsnag to drop the event entirely.
 */
const onError: OnErrorCallback = (event) => {
  const url = event.request?.url ?? ""
  if (url.includes(".translate.goog")) return false
}

if (apiKey) {
  Bugsnag.start({
    apiKey,
    appVersion: publicRuntimeConfig.GIT_COMMIT,
    releaseStage: publicRuntimeConfig.APP_ENV,
    enabledReleaseStages: ["staging", "production"],
    collectUserIp: false,
    plugins: [new BugsnagPluginReact()],
    onError,
  })
}

export { Bugsnag }
